import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { platform } from './identity';

const PUBLIC_WEB_HOSTS = new Set(['zodiacus.app', 'www.zodiacus.app']);
const EXTERNAL_WEB_PATHS = new Set(['/verify-email', '/reset-password', '/privacidad', '/terminos']);
const ROOT_ROUTES = new Set(['/', '/welcome', '/login', '/register', '/home']);
const fallbackByPrefix = [
  ['/zodiac-crush/play', '/zodiac-crush'],
  ['/zodiac-crush/map', '/home'],
  ['/zodiac-crush', '/home'],
  ['/chat/', '/contacts'],
  ['/foro/', '/foro'],
  ['/profile/', '/home'],
];

export function classifyUrl(raw, base = 'https://zodiacus.app') {
  const value = String(raw || '').trim();
  if (!value) return { kind: 'unsafe', reason: 'empty' };
  if (value.startsWith('/') && !value.startsWith('//')) return { kind: 'internal', path: value };

  let url;
  try { url = new URL(value, base); } catch { return { kind: 'unsafe', reason: 'malformed' }; }
  if (!['http:', 'https:'].includes(url.protocol)) return { kind: 'unsafe', reason: 'scheme' };
  if (url.hostname === 'api.zodiacus.app') return { kind: 'api', url: url.toString() };
  if (PUBLIC_WEB_HOSTS.has(url.hostname) && !EXTERNAL_WEB_PATHS.has(url.pathname)) {
    return { kind: 'internal', path: `${url.pathname}${url.search}${url.hash}` };
  }
  return { kind: 'external', url: url.toString() };
}

export async function openExternal(raw, { native = platform.isNative, browser = Browser, win = typeof window !== 'undefined' ? window : undefined } = {}) {
  const classified = classifyUrl(raw);
  if (classified.kind !== 'external') return false;
  if (native) {
    if (!classified.url.startsWith('https://')) return false;
    await browser.open({ url: classified.url, presentationStyle: 'popover' });
  } else {
    win?.open?.(classified.url, '_blank', 'noopener,noreferrer');
  }
  return true;
}

export function getBackAction(pathname, { stackDepth = 1 } = {}) {
  const path = String(pathname || '/').split(/[?#]/)[0] || '/';
  if (ROOT_ROUTES.has(path)) return { type: 'exit' };
  if (stackDepth > 1) return { type: 'back' };
  const match = fallbackByPrefix.find(([prefix]) => path === prefix || path.startsWith(prefix));
  return { type: 'navigate', to: match?.[1] || '/home' };
}

const dismissHandlers = [];
export function registerBackHandler(handler) {
  dismissHandlers.push(handler);
  return () => {
    const index = dismissHandlers.lastIndexOf(handler);
    if (index >= 0) dismissHandlers.splice(index, 1);
  };
}

export async function dispatchBack({ pathname, stackDepth, navigate, exitApp = () => App.exitApp() }) {
  for (let index = dismissHandlers.length - 1; index >= 0; index -= 1) {
    if (await dismissHandlers[index]()) return 'dismissed';
  }
  const action = getBackAction(pathname, { stackDepth });
  if (action.type === 'back') navigate(-1);
  else if (action.type === 'navigate') navigate(action.to, { replace: true });
  else await exitApp();
  return action.type;
}

export function subscribeNativeBack(listener, { native = platform.isAndroid, app = App } = {}) {
  if (!native) return () => {};
  let handle;
  let cancelled = false;
  Promise.resolve(app.addListener('backButton', listener)).then((value) => {
    if (cancelled) value?.remove?.();
    else handle = value;
  });
  return () => { cancelled = true; handle?.remove?.(); };
}

export function subscribeAppUrl(listener, { native = platform.isNative, app = App } = {}) {
  if (!native) return () => {};
  let handle;
  let cancelled = false;
  Promise.resolve(app.addListener('appUrlOpen', ({ url }) => listener(classifyUrl(url)))).then((value) => {
    if (cancelled) value?.remove?.();
    else handle = value;
  });
  return () => { cancelled = true; handle?.remove?.(); };
}
